import { Building2, Mail, ArrowRight } from 'lucide-react';
import { ByteLogo } from './ByteLogo';

interface LoginPageProps {
  onMAppLogin: () => void;
  onEmailLogin: () => void;
  isLoading?: boolean;
}

export function LoginPage({ onMAppLogin, onEmailLogin, isLoading }: LoginPageProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex mb-4 justify-center">
            <ByteLogo className="w-16 h-16" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Welcome to Byte Quality Complaints
          </h1>
          <p className="text-gray-600">
            Choose how you'd like to sign in
          </p>
        </div>

        {/* Login Options */}
        <div className="space-y-4">
          {/* MicroApp Login Button */}
          <button
            onClick={onMAppLogin}
            disabled={isLoading}
            data-testid="login-btn-mapp"
            className="w-full group relative bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-5 hover:border-primary-500 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-600 transition-colors">
                <Building2 className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900 text-lg">
                  Sign in with MicroApp
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  For Yum! employees with Byte Coach access
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
            </div>
          </button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gradient-to-br from-orange-50 via-white to-purple-50 text-gray-500">
                or
              </span>
            </div>
          </div>

          {/* Email Login Button */}
          <button
            onClick={onEmailLogin}
            disabled={isLoading}
            data-testid="login-btn-email"
            className="w-full group relative bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-5 hover:border-purple-500 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-600 transition-colors">
                <Mail className="w-6 h-6 text-purple-600 group-hover:text-white transition-colors" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900 text-lg">
                  Sign in with Email
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  For vendors and external partners
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
            </div>
          </button>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-500 mt-8">
          Need help?{' '}
          <a href="https://yum.service-now.com/rt" className="text-primary-600 hover:underline">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
